import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../supabaseClient'

const GROUPS = ['A','B','C','D','E','F','G','H','I','J','K','L']

function getWinner(pred, match) {
  if (!pred || pred.home === '' || pred.away === '' || pred.home === undefined || pred.away === undefined) return null
  const h = parseInt(pred.home)
  const a = parseInt(pred.away)
  if (isNaN(h) || isNaN(a)) return null
  if (h > a) return match.home
  if (a > h) return match.away
  if (h === a && pred.penalties_winner) {
    if (pred.penalties_winner === 'home') return match.home
    if (pred.penalties_winner === 'away') return match.away
  }
  return null
}

function getLoser(pred, match) {
  const winner = getWinner(pred, match)
  if (!winner) return null
  if (winner.id === match.home?.id) return match.away
  return match.home
}

function buildBracket(matches, predictions) {
  const resolved = {}
  const r32 = matches.filter(m => m.phase === 'knockout_r32')
  r32.forEach(m => { resolved[m.match_number] = { ...m, home: m.home || null, away: m.away || null } })

  const r16pairs = [[74,77],[73,75],[76,78],[79,80],[83,84],[81,82],[86,88],[85,87]]
  matches.filter(m => m.phase === 'knockout_r16').forEach((m, i) => {
    const m1 = resolved[r16pairs[i][0]]
    const m2 = resolved[r16pairs[i][1]]
    resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
  })

  const qfpairs = [[89,90],[93,94],[91,92],[95,96]]
  matches.filter(m => m.phase === 'knockout_qf').forEach((m, i) => {
    const m1 = resolved[qfpairs[i][0]]
    const m2 = resolved[qfpairs[i][1]]
    resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
  })

  const sfpairs = [[97,98],[99,100]]
  matches.filter(m => m.phase === 'knockout_sf').forEach((m, i) => {
    const m1 = resolved[sfpairs[i][0]]
    const m2 = resolved[sfpairs[i][1]]
    resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
  })

  const finalMatch = matches.find(m => m.phase === 'knockout_final')
  if (finalMatch) {
    const sf1 = resolved[101]
    const sf2 = resolved[102]
    resolved[finalMatch.match_number] = { ...finalMatch, home: sf1 ? getWinner(predictions[sf1.id], sf1) : null, away: sf2 ? getWinner(predictions[sf2.id], sf2) : null }
  }

  const thirdMatch = matches.find(m => m.phase === 'knockout_3rd')
  if (thirdMatch) {
    const sf1 = resolved[101]
    const sf2 = resolved[102]
    resolved[thirdMatch.match_number] = { ...thirdMatch, home: sf1 ? getLoser(predictions[sf1.id], sf1) : null, away: sf2 ? getLoser(predictions[sf2.id], sf2) : null }
  }

  return resolved
}

export default function MyPredictions() {
  const { entryId } = useParams()
  const [entry, setEntry] = useState(null)
  const [predictions, setPredictions] = useState([])
  const [bonuses, setBonuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeGroup, setActiveGroup] = useState('A')
  const [phase2Locked, setPhase2Locked] = useState(false) // eslint-disable-line no-unused-vars
  const [koMatches, setKoMatches] = useState([])
  const [koPredMap, setKoPredMap] = useState({})
  const [bracket, setBracket] = useState({})

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { window.location.href = '/login'; return }
      const { data: entryData, error: entryError } = await supabase.from('entries').select('*').eq('id', entryId).eq('user_id', user.id).single()
      if (entryError || !entryData) { setError('Entry not found.'); setLoading(false); return }
      setEntry(entryData)
      const { data: phase2Setting } = await supabase.from('settings').select('value').eq('key', 'phase2_open').single()
      const phase2Open = phase2Setting?.value === 'true'
      setPhase2Locked(entryData.phase2_locked || !phase2Open)
      const { data: predsData } = await supabase.from('match_predictions').select(`*, matches(match_number, group_code, phase, actual_home_goals, actual_away_goals, result_confirmed, home:home_team_id(name, flag_emoji), away:away_team_id(name, flag_emoji))`).eq('entry_id', entryId)
      const sorted = (predsData || []).sort((a, b) => (a.matches?.match_number || 0) - (b.matches?.match_number || 0))
      setPredictions(sorted)

      const { data: koMatchData } = await supabase
        .from('matches')
        .select('*, home:home_team_id(id,name,flag_emoji), away:away_team_id(id,name,flag_emoji)')
        .in('phase', ['knockout_r32','knockout_r16','knockout_qf','knockout_sf','knockout_3rd','knockout_final'])
        .order('match_number')
      setKoMatches(koMatchData || [])

      const predMap = {}
      predsData?.forEach(p => {
        predMap[p.match_id] = {
          home: p.predicted_home_goals,
          away: p.predicted_away_goals,
          penalties_winner: p.predicted_penalties_winner || null
        }
      })
      setKoPredMap(predMap)
      setBracket(buildBracket(koMatchData || [], predMap))
      const { data: bonusData } = await supabase.from('bonus_predictions').select('*').eq('entry_id', entryId)
      setBonuses(bonusData || [])
      setLoading(false)
    }
    load()
  }, [entryId])

  if (loading) return <div><nav className="topnav"><a href="/" className="topnav-logo">PREDICTA</a></nav><div className="container"><p className="text-muted">Loading...</p></div></div>
  if (error) return <div><nav className="topnav"><a href="/" className="topnav-logo">PREDICTA</a></nav><div className="container"><div className="card"><p style={{ color: 'var(--red)' }}>{error}</p></div></div></div>

  if (!entry.paid) return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
        <a href="/world-cup" className="btn btn-secondary" style={{ padding: '7px 16px', fontSize: '13px' }}>Dashboard</a>
      </nav>
      <div style={{ textAlign: 'center', padding: '80px 24px 0' }}>
        <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔒</div>
        <h1 style={{ fontFamily: 'var(--ff-d)', fontSize: '36px', marginBottom: '8px' }}>{entry.entry_name}</h1>
        <p style={{ color: 'var(--text-2)', maxWidth: '400px', margin: '0 auto 32px', lineHeight: '1.7' }}>
          Your predictions have been saved. Points will display and your entry will appear on the leaderboard once payment is confirmed by the organiser.
        </p>
        <a href="/world-cup" className="btn btn-secondary">Back to dashboard</a>
      </div>
    </div>
  )

  const totalPoints = predictions.reduce((sum, p) => sum + (p.points_awarded || 0), 0) + bonuses.reduce((sum, b) => sum + (b.points_awarded || 0), 0)
  const confirmedMatches = predictions.filter(p => p.matches?.result_confirmed)
  const pointsScored = confirmedMatches.filter(p => p.points_awarded > 0).length
  const groupPredictions = (group) => predictions.filter(p => p.matches?.group_code === group)

  const categoryLabels = { golden_boot: 'Golden Boot', golden_gloves: 'Golden Gloves', player_tournament: 'Player of the Tournament' }

  const statusConfig = {
    exact: { label: 'Exact', bg: 'var(--green-bg)', col: 'var(--green)' },
    result: { label: 'Result', bg: 'var(--amber-bg)', col: 'var(--amber)' },
    miss: { label: 'Miss', bg: 'rgba(0,0,0,.05)', col: 'var(--text-3)' },
  }

  function getStatus(p) {
    if (!p.matches?.result_confirmed) return null
    if (p.points_awarded >= 5) return 'exact'
    if (p.points_awarded > 0) return 'result'
    return 'miss'
  }

  return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a href="/world-cup" className="btn btn-secondary" style={{ padding: '7px 14px', fontSize: '13px' }}>Dashboard</a>
          <a href={`/entry/${entryId}`} className="btn btn-secondary" style={{ padding: '7px 14px', fontSize: '13px' }}>Edit</a>
        </div>
      </nav>

      <div className="container">
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '28px', marginBottom: '4px' }}>{entry.entry_name}</h1>
          <p style={{ color: 'var(--text-2)' }}>My predictions and points</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: '10px', marginBottom: '28px' }}>
          {[[totalPoints, 'Total Points', 'var(--gold)'], [confirmedMatches.length, 'Confirmed', 'var(--text)'], [pointsScored, 'Scored', 'var(--green)']].map(([v, l, c]) => (
            <div key={l} className="stat-card">
              <div className="stat-num" style={{ color: c }}>{v}</div>
              <div className="stat-label">{l}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {GROUPS.map(g => {
            const gPts = groupPredictions(g).reduce((sum, p) => sum + (p.points_awarded || 0), 0)
            return (
              <button key={g} onClick={() => setActiveGroup(g)} style={{
                padding: '6px 14px', borderRadius: 'var(--r-sm)', fontSize: '13px', cursor: 'pointer',
                fontFamily: 'var(--ff-d)', fontWeight: '600', letterSpacing: '.04em',
                background: activeGroup === g ? 'var(--gold-bg)' : 'transparent',
                color: activeGroup === g ? 'var(--gold)' : 'var(--text-2)',
                border: activeGroup === g ? '1px solid var(--gold-border)' : '1px solid var(--border)',
              }}>
                Group {g} {gPts > 0 && <span style={{ fontSize: '11px' }}>({gPts})</span>}
              </button>
            )
          })}
        </div>

        <div className="card-flat" style={{ marginBottom: '14px' }}>
          <table>
            <thead>
              <tr>
                <th>Match</th>
                <th style={{ textAlign: 'center' }}>My Prediction</th>
                <th style={{ textAlign: 'center' }}>Actual</th>
                <th style={{ textAlign: 'center', width: '100px' }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {groupPredictions(activeGroup).length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', color: 'var(--text-3)', padding: '24px' }}>No predictions for this group.</td></tr>
              ) : (
                groupPredictions(activeGroup).map(p => {
                  const status = getStatus(p)
                  const cfg = status ? statusConfig[status] : null
                  return (
                    <tr key={p.id}>
                      <td style={{ fontSize: '13px' }}>
                        {p.matches?.home?.flag_emoji} {p.matches?.home?.name} vs {p.matches?.away?.name} {p.matches?.away?.flag_emoji}
                      </td>
                      <td style={{ textAlign: 'center', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '16px' }}>
                        {p.predicted_home_goals} - {p.predicted_away_goals}
                      </td>
                      <td style={{ textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '16px' }}>
                        {p.matches?.result_confirmed ? `${p.matches.actual_home_goals} - ${p.matches.actual_away_goals}` : '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {cfg ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.06em', background: cfg.bg, color: cfg.col }}>{cfg.label}</span>
                            <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '18px', color: cfg.col }}>{p.points_awarded > 0 ? `+${p.points_awarded}` : '0'}</span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-3)' }}>-</span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {bonuses.length > 0 && (
          <div className="card-flat">
            <table>
              <thead>
                <tr>
                  <th>Bonus Category</th>
                  <th>My Answer</th>
                  <th style={{ textAlign: 'center' }}>Points</th>
                </tr>
              </thead>
              <tbody>
                {bonuses.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontSize: '13px' }}>{categoryLabels[b.category] || b.category}</td>
                    <td style={{ fontSize: '13px', color: b.is_correct ? 'var(--green)' : 'var(--text)', fontWeight: b.is_correct ? '600' : '400' }}>
                      {b.predicted_player_name} {b.is_correct && '✓'}
                    </td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '18px', color: b.points_awarded > 0 ? 'var(--gold)' : 'var(--text-3)' }}>
                      {b.points_awarded > 0 ? `+${b.points_awarded}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {predictions.filter(p => p.matches?.phase !== 'group').length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', marginBottom: '16px' }}>Knockout Stage</h2>
            {['knockout_r32','knockout_r16','knockout_qf','knockout_sf','knockout_3rd','knockout_final'].map(phase => {
              const phaseLabels = { knockout_r32: 'Round of 32', knockout_r16: 'Round of 16', knockout_qf: 'Quarter-finals', knockout_sf: 'Semi-finals', knockout_3rd: '3rd Place Play-off', knockout_final: 'Final' }
              const phasePreds = predictions.filter(p => p.matches?.phase === phase)
              if (phasePreds.length === 0) return null
              return (
                <div key={phase} style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>{phaseLabels[phase]}</p>
                  <div className="card-flat">
                    <table>
                      <thead>
                        <tr>
                          <th>Match</th>
                          <th style={{ textAlign: 'center' }}>My Prediction</th>
                          <th style={{ textAlign: 'center' }}>Actual</th>
                          <th style={{ textAlign: 'center', width: '100px' }}>Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {phasePreds.map(p => {
                          const status = getStatus(p)
                          const cfg = status ? statusConfig[status] : null
                          const koMatch = koMatches.find(m => m.id === p.match_id)
                          const resolved = bracket[koMatch?.match_number] || {}
                          const homeTeam = resolved.home || p.matches?.home
                          const awayTeam = resolved.away || p.matches?.away
                          const pred = koPredMap[p.match_id] || {}
                          const isDraw = p.predicted_home_goals !== null && p.predicted_away_goals !== null && p.predicted_home_goals === p.predicted_away_goals
                          const penWinner = pred.penalties_winner === 'home' ? homeTeam : pred.penalties_winner === 'away' ? awayTeam : null
                          return (
                            <tr key={p.id}>
                              <td style={{ fontSize: '13px' }}>
                                <div>{homeTeam?.flag_emoji} {homeTeam?.name || '?'} vs {awayTeam?.name || '?'} {awayTeam?.flag_emoji}</div>
                                {isDraw && penWinner && (
                                  <div style={{ fontSize: '11px', color: 'var(--gold)', marginTop: '3px' }}>
                                    Pen: {penWinner.flag_emoji} {penWinner.name}
                                  </div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '16px' }}>
                                {p.predicted_home_goals} - {p.predicted_away_goals}
                              </td>
                              <td style={{ textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '16px' }}>
                                {p.matches?.result_confirmed ? `${p.matches.actual_home_goals} - ${p.matches.actual_away_goals}` : '-'}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {cfg ? (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.06em', background: cfg.bg, color: cfg.col }}>{cfg.label}</span>
                                    <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '18px', color: cfg.col }}>{p.points_awarded > 0 ? `+${p.points_awarded}` : '0'}</span>
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-3)' }}>-</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}